import React, { useEffect } from 'react';
import store, { searchActions, loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';

import LoanInsuranceTable from './tables/LoanInsuranceTable';
import { InsuranceApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { getGridTypeForPageType, getLoanTypeForPageType } from '../common/LoanFunctions';
import { LoanPageType, LoanGridType, LoanPageOptions } from '../common/LoanConstants';
import { InsuranceTableTypeOptions } from '../insurance/common/InsuranceConstants';
import { Accordion } from '../../common/SharedComponents';
import debounce from 'lodash.debounce';
import { BfiCustomStore, HelpText } from '../../common/SharedComponents';
import { LoanModals } from '../common/LoanComponents';
import { BorrowerModals } from '../../borrowers/common/BorrowerComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';

const InsuranceLandingPage = ({ pageType = LoanPageType.Landing }) => {
    const searchState = useSelector((state) => state.searchState);
    const styles = {
        bigRedDot: {
            background: '#E00400',
            height: '16px',
            width: '16px',
            display: 'inline-block',
            borderRadius: '8px',
            border: '1px solid silver',
            float: 'left',
        },
        bigYellowDot: {
            background: 'yellow',
            height: '16px',
            width: '16px',
            display: 'inline-block',
            borderRadius: '8px',
            border: '1px solid silver',
            float: 'left',
        },
        keyDescription: {
            marginLeft: '30px',
        },
        keyRow: { textAlign: 'left', padding: '10px' },
        keyBox: {
            width: '450px',
            border: '1px solid silver',
            padding: '20px',
            borderRadius: '5px',
            margin: 'auto',
        },
        subHeading: {
            color: '#2c5591',
            paddingLeft: '25px',
            fontSize: '14px',
            marginBottom: '2px',
            paddingTop: '2px',
        },
        card: { borderRadius: 'initial', marginBottom: '5px' },
        section: {
            backgroundColor: 'black',
            color: 'white',
            paddingLeft: '5px',
            fontSize: '14px',
            marginBottom: '0',
        },
    };

    const { dispatch } = store;
    const insuranceApi = new InsuranceApi.InsuranceApi(applicationInsights);
    const locExportType = '0';

    //Needs to be created outside of the Table component otherwise every re-render of the table causes a data retrieval
    const gridStore = new BfiCustomStore();

    useEffect(() => {
        if (!pageType) {
            return;
        }

        return () => {
            //Cancel any further searches if component is unloaded
            debouncedSearch.cancel();
        };
    }, []);

    const debouncedSearch = debounce(dispatch, 500);

    const _onSearchChange = (event) => {
        debouncedSearch(
            searchActions.setSearchText({
                searchText: {
                    ...searchState.searchText,
                    loanInsurance: event.target.value,
                },
            })
        );
    };

    const addLoan = (pageType) => {
        if (pageType === LoanPageType.LOC) {
            dispatch(
                loanActions.setEditMasterNoteModal({
                    editMasterNoteModal: {
                        isOpen: true,
                        loanId: 0,
                        showInsurance: true,
                    },
                })
            );
        } else {
            const loanTypeId = getLoanTypeForPageType(pageType);
            dispatch(
                loanActions.setEditStdLoanModal({
                    editStdLoanModal: {
                        isOpen: true,
                        loanId: 0,
                        showInsurance: true,
                        loanType: loanTypeId,
                    },
                })
            );
        }
    };

    const _export = (pageType) => {
        const loanTypeId = pageType === LoanPageType.LOC ? locExportType : getLoanTypeForPageType(pageType);
        insuranceApi.exportInsurance(loanTypeId);
    };

    const renderTables = (insTableType) => {
        const t1GridType = pageType === LoanPageType.LOC ? LoanGridType.MasterNotes : getGridTypeForPageType(pageType);
        return (
            <Accordion
                title={insTableType.name}
                subTitle={''}
                isExpanded={insTableType.isExpanded}
                //Set the id so that the accordian resets state when the user moves to a different Loan Page
                id={
                    pageType === LoanPageType.LOC
                        ? `acc_Ins_MN_${insTableType.id}`
                        : `accSL_${getGridTypeForPageType(pageType)}_${insTableType.id}`
                }
            >
                {pageType === LoanPageType.LOC && <h6 style={styles.subHeading}>Master Notes</h6>}
                <div
                    className='card'
                    style={styles.card}
                >
                    <LoanInsuranceTable
                        tableType={insTableType}
                        gridType={t1GridType}
                        gridStore={gridStore}
                    />
                </div>
                {pageType === LoanPageType.LOC && (
                    <>
                        <h6 style={styles.subHeading}>Sub-Notes</h6>
                        <div
                            className='card'
                            style={styles.card}
                        >
                            <LoanInsuranceTable
                                tableType={insTableType}
                                gridType={LoanGridType.SubNotes}
                                gridStore={gridStore}
                            />
                        </div>
                    </>
                )}
            </Accordion>
        );
    };

    return (
        <>
            <div
                className='page-header'
                role='banner'
            >
                <h1>
                    Insurance Manager
                    {pageType === LoanPageType.Landing ? (
                        ''
                    ) : (
                        <span>
                            &nbsp;&nbsp;-
                            <b>&nbsp;&nbsp;{LoanPageOptions[pageType].name}</b>
                        </span>
                    )}
                </h1>
                <div className='primary-actions'>
                    {pageType !== LoanPageType.Landing && (
                        <>
                            <button
                                className='hollow'
                                onClick={() => addLoan(pageType)}
                                style={{ padding: '10px 20px' }}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                {pageType === LoanPageType.LOC ? ' Add Master Note' : ' Add Loan'}
                            </button>
                            &nbsp;&nbsp;
                            <button
                                className='hollow'
                                onClick={() => _export(pageType)}
                                style={{ padding: '10px 20px' }}
                            >
                                <FontAwesomeIcon icon={faDownload} /> Export
                            </button>
                            &nbsp;
                            <HelpText />
                        </>
                    )}
                </div>
            </div>
            <div
                className='main-content'
                style={{ padding: 0, backgroundColor: '#f4f4f4' }}
            >
                {pageType !== LoanPageType.Landing && (
                    <div className='input-has-icon main-search'>
                        <input
                            type='search'
                            placeholder='Search on BFI Number, loan number, city, state, borrower or user...'
                            defaultValue={searchState?.searchText?.loanInsurance}
                            onChange={(event) => _onSearchChange(event)}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            aria-hidden='true'
                            className='icon'
                        />
                    </div>
                )}
                <div
                    className='loans scrollable-content'
                    style={{ padding: '.5rem' }}
                >
                    {pageType === LoanPageType.Landing && (
                        <div style={{ textAlign: 'center' }}>
                            <h5>Manage Insurance on Loans Before and After Closing</h5>
                            <br />
                            <div style={styles.keyBox}>
                                <h6>Key</h6>
                                <div style={styles.keyRow}>
                                    <div style={styles.bigRedDot}></div>
                                    <div style={styles.keyDescription}>
                                        Outstanding loan(s) have expired insurance or missing insurance Expiration Date/Amount
                                    </div>
                                </div>
                                <br />
                                <div style={styles.keyRow}>
                                    <div style={styles.bigYellowDot}></div>
                                    <div style={styles.keyDescription}>
                                        Outstanding loan(s) DO NOT have expired insurance but DO have expiration(s) in {'<'} 30
                                        days
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {pageType !== LoanPageType.Landing && (
                        <div
                            className='accordion'
                            data-accordion=''
                            data-allow-all-closed='true'
                            data-multi-expand='true'
                        >
                            <h6 style={styles.section}>Before Closing</h6>
                            {renderTables(InsuranceTableTypeOptions.BeforeClosingMissing)}
                            {renderTables(InsuranceTableTypeOptions.BeforeClosingGood)}
                            <h6 style={styles.section}>After Closing</h6>
                            {renderTables(InsuranceTableTypeOptions.ClosedMissing)}
                            {renderTables(InsuranceTableTypeOptions.ClosedGood)}
                            <h6 style={styles.section}>Repaid</h6>
                            {renderTables(InsuranceTableTypeOptions.Repaid)}
                        </div>
                    )}
                </div>
            </div>
            <LoanModals />
            <BorrowerModals />
        </>
    );
};

export default InsuranceLandingPage;
